import { useCallback, useMemo } from 'react';
import {
  useReduceWithModel,
  IModel,
} from '@/hooks/useReduceWithModel/useReduceWithModel';
import {
  IFetchConfirmOrder,
  IFetchMilkAddrLast,
  IFetchShoppingList,
} from '@/services/productTypes';

interface IState {
  confirmData: IFetchConfirmOrder;
}

const initialState: IState = {
  confirmData: {
    addressId: undefined,
    needMilkBox: undefined,
    goods: [],
    addressDetail: {},
    couponData: {},
    allAddressList: [],
    stocks: {},
    comments: [],
    shippings: [],
    mobile: undefined,
    isSeckill: false,
  },
};
/**
 * 可以直接返回state，也可以修改draft里面的数据，返回空
 * @param draft
 * @param action
 */

enum actionEnum {
  initalialData = 'initalialData',
  initAddss = 'initAddss',
  initCoupon = 'initCoupon',
  initneedMilkBox = 'initneedMilkBox',
  initAllAdressList = 'initAllAdressList',
  // updateRemark = 'updateRemark',
  reset = 'reset',
  initStocks = 'initStocks',
  updateGoods = 'updateGoods',
  initShippings = 'initShippings',
  initMobile = 'initMobile',
  initSeckill = 'initSeckill',
}
const model: IModel<IState> = {
  immer: true,
  state: initialState,
  reducers: {
    [actionEnum.initalialData](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          goods: action.payload,
          // 重置其他编辑数据
          addressId: undefined,
          needMilkBox: undefined,
          addressDetail: {},
          couponData: {},
          allAddressList: [],
          stocks: {},
          comments: [],
          shippings: [],
          mobile: undefined,
        },
      };
    },
    [actionEnum.initAddss](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          addressDetail: action.payload,
        },
      };
    },
    [actionEnum.initCoupon](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      // 使用首次加载是获取到的数据，后续调用优惠券接口不回再返回这些数据
      const coupon_supplier_ids =
        action.payload.coupon_supplier_ids ||
        state.confirmData.couponData.coupon_supplier_ids;
      const has_platform_coupon = [true, false].includes(
        action.payload?.has_platform_coupon,
      )
        ? action.payload?.has_platform_coupon
        : state.confirmData.couponData.has_platform_coupon;
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          couponData: {
            // 保留类型，以便后续的操作
            updateType: state.confirmData.couponData.updateType,
            ...(action.payload || {}),
            has_platform_coupon,
            coupon_supplier_ids,
          },
        },
      };
    },
    [actionEnum.reset](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {},
      };
    },
    [actionEnum.initneedMilkBox](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          needMilkBox: action.payload,
        },
      };
    },
    [actionEnum.initAllAdressList](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          allAddressList: action.payload,
        },
      };
    },
    [actionEnum.initStocks](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          stocks: action.payload,
        },
      };
    },
    [actionEnum.updateGoods](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          goods: action.payload,
        },
      };
    },
    [actionEnum.initShippings](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          shippings: action.payload,
        },
      };
    },
    [actionEnum.initMobile](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          mobile: action.payload,
        },
      };
    },
    [actionEnum.initSeckill](
      state: IState,
      action: { type: actionEnum; [extraProps: string]: any },
    ) {
      return {
        ...state,
        confirmData: {
          ...state.confirmData,
          isSeckill: action.payload,
        },
      };
    },
  },
};
const useConfirmOrderModel = () => {
  const [state, dispatch] = useReduceWithModel(model);

  // 页面初始化缓存去人订单的所有数据
  const initalial = useCallback(
    (shoppingGoodsList: IFetchShoppingList) => {
      dispatch({ type: actionEnum.initalialData, payload: shoppingGoodsList });
    },
    [dispatch],
  );
  // 缓存地址id;
  const initAddss = useCallback(
    (addressDetail: IFetchMilkAddrLast['result']) => {
      dispatch({ type: actionEnum.initAddss, payload: addressDetail });
    },
    [dispatch],
  );
  // 缓存优惠券;
  const initCoupon = useCallback(
    (coupon: IFetchConfirmOrder['couponData']) => {
      dispatch({ type: actionEnum.initCoupon, payload: coupon });
    },
    [dispatch],
  );
  // 更新是否需要箱子;
  const initneedMilkBox = useCallback(
    (needMilkBox: boolean) => {
      dispatch({ type: actionEnum.initneedMilkBox, payload: needMilkBox });
    },
    [dispatch],
  );
  // 缓存所有地址列表;
  const initAllAdressList = useCallback(
    (addressList: boolean) => {
      dispatch({ type: actionEnum.initAllAdressList, payload: addressList });
    },
    [dispatch],
  );
  // 根据运费缓存商品的库存;
  const initStocks = useCallback(
    (stocks: any) => {
      dispatch({ type: actionEnum.initStocks, payload: stocks });
    },
    [dispatch],
  );
  // 更新留言;
  const updateGoods = useCallback(
    (comments: any) => {
      dispatch({ type: actionEnum.updateGoods, payload: comments });
    },
    [dispatch],
  );
  // 更新运费;
  const initShippings = useCallback(
    (shippings: any) => {
      dispatch({ type: actionEnum.initShippings, payload: shippings });
    },
    [dispatch],
  );
  // 重置数据
  const reset = useCallback(
    (confirmData: IFetchConfirmOrder) => {
      dispatch({ type: actionEnum.reset, payload: confirmData });
    },
    [dispatch],
  );
  const initMobile = useCallback(
    (mobile: number | undefined | string) => {
      dispatch({ type: actionEnum.initMobile, payload: mobile });
    },
    [dispatch],
  );
  const initSeckill = useCallback(
    (isSeckillFlag: boolean) => {
      dispatch({ type: actionEnum.initSeckill, payload: isSeckillFlag });
    },
    [dispatch],
  );
  const goods = useMemo<IFetchConfirmOrder['goods']>(
    () => state.confirmData.goods || [],
    [state],
  );
  const addressDetail = useMemo<IFetchConfirmOrder['addressDetail']>(
    () => state.confirmData.addressDetail || {},
    [state],
  );
  const couponData = useMemo<IFetchConfirmOrder['couponData']>(
    () => state.confirmData.couponData || {},
    [state],
  );
  const addressList = useMemo<IFetchConfirmOrder['allAddressList']>(
    () => state.confirmData.allAddressList || [],
    [state],
  );
  const stocks = useMemo(() => state.confirmData.stocks, [state]);
  const remarks = useMemo(() => state.confirmData.comments, [state]);
  const shippings = useMemo<IFetchConfirmOrder['shippings']>(
    () => state.confirmData.shippings,
    [state],
  );
  const mobile = useMemo(() => state.confirmData.mobile, [state]);
  const isSeckill = useMemo(() => state.confirmData.isSeckill, [state]);

  return {
    state,
    goods,
    addressDetail,
    couponData,
    addressList,
    stocks,
    remarks,
    shippings,
    mobile,
    isSeckill,
    initShippings,
    updateGoods,
    initalial,
    reset,
    initAddss,
    initCoupon,
    initneedMilkBox,
    initAllAdressList,
    initStocks,
    initMobile,
    initSeckill,
  };
};

export default useConfirmOrderModel;
